/**
 * FathomVoyage — Bunker Price Monitor
 * Mock Data Service — Realistic April 2026 pricing
 *
 * Data context:
 *  - Iran-US tensions elevating Fujairah/Jeddah/Red Sea prices
 *  - Many vessels rerouting via Cape → Cape Town/Durban demand spike
 *  - VLSFO range: $680–780/mt globally
 *  - HSFO range: $440–475/mt
 *  - MGO range: $810–885/mt
 *  - LNG ($/mt equivalent): $655–700/mt (select ports only)
 */

const generateHistory = (currentPrice, volatility = 8, days = 7) => {
  const history = [currentPrice];
  for (let i = 1; i < days; i++) {
    const prev = history[0];
    const move = (Math.random() - 0.5) * volatility * 2;
    history.unshift(Math.round((prev - move) * 10) / 10);
  }
  return history; // oldest → newest, last element = current
};

export const PORTS_DATA = {
  SGP: {
    name: 'Singapore',
    region: 'Asia Pacific',
    flag: '🇸🇬',
    lat: 1.29, lng: 103.85,
    VLSFO: { price: 728, change: -8,  history: [743, 748, 750, 745, 740, 736, 728] },
    HSFO:  { price: 458, change: +3,  history: [451, 449, 452, 455, 460, 455, 458] },
    MGO:   { price: 848, change: -5,  history: [861, 865, 860, 857, 854, 853, 848] },
    LNG:   { price: 695, change: -2,  history: [703, 705, 702, 700, 698, 697, 695] },
  },
  FUJ: {
    name: 'Fujairah',
    region: 'Middle East',
    flag: '🇦🇪',
    lat: 25.12, lng: 56.34,
    // Elevated — war risk proximity, Hormuz tensions
    VLSFO: { price: 762, change: +12, history: [741, 738, 742, 748, 754, 750, 762] },
    HSFO:  { price: 471, change: +8,  history: [454, 452, 456, 460, 466, 463, 471] },
    MGO:   { price: 882, change: +9,  history: [861, 858, 863, 869, 875, 873, 882] },
    LNG:   { price: null, change: null, history: null },
  },
  RTM: {
    name: 'Rotterdam',
    region: 'North Europe',
    flag: '🇳🇱',
    lat: 51.92, lng: 4.47,
    VLSFO: { price: 694, change: -4,  history: [705, 708, 706, 702, 700, 698, 694] },
    HSFO:  { price: 441, change: -2,  history: [448, 447, 446, 445, 444, 443, 441] },
    MGO:   { price: 812, change: -3,  history: [822, 820, 818, 817, 816, 815, 812] },
    LNG:   { price: 672, change: +4,  history: [665, 664, 666, 667, 668, 668, 672] },
  },
  HOU: {
    name: 'Houston',
    region: 'US Gulf',
    flag: '🇺🇸',
    lat: 29.75, lng: -95.37,
    VLSFO: { price: 702, change: +3,  history: [694, 695, 697, 699, 700, 699, 702] },
    HSFO:  { price: 447, change: +5,  history: [438, 437, 440, 442, 444, 442, 447] },
    MGO:   { price: 823, change: +6,  history: [812, 811, 814, 816, 818, 817, 823] },
    LNG:   { price: 658, change: +2,  history: [652, 651, 653, 654, 655, 656, 658] },
  },
  PIR: {
    name: 'Piraeus',
    region: 'Mediterranean',
    flag: '🇬🇷',
    lat: 37.94, lng: 23.63,
    VLSFO: { price: 718, change: -2,  history: [724, 726, 725, 723, 722, 720, 718] },
    HSFO:  { price: 451, change:  0,  history: [450, 451, 452, 451, 450, 451, 451] },
    MGO:   { price: 836, change: -4,  history: [844, 843, 842, 841, 840, 840, 836] },
    LNG:   { price: null, change: null, history: null },
  },
  GIB: {
    name: 'Gibraltar',
    region: 'Med / Atlantic',
    flag: '🇬🇮',
    lat: 36.14, lng: -5.35,
    VLSFO: { price: 711, change: -1,  history: [716, 717, 715, 714, 713, 712, 711] },
    HSFO:  { price: 445, change: +2,  history: [440, 441, 442, 443, 443, 443, 445] },
    MGO:   { price: 828, change: +2,  history: [823, 822, 824, 825, 826, 826, 828] },
    LNG:   { price: null, change: null, history: null },
  },
  ALG: {
    name: 'Algeciras',
    region: 'Med / Atlantic',
    flag: '🇪🇸',
    lat: 36.14, lng: -5.45,
    VLSFO: { price: 708, change: -3,  history: [716, 715, 714, 713, 712, 711, 708] },
    HSFO:  { price: 443, change: +1,  history: [439, 440, 441, 442, 442, 442, 443] },
    MGO:   { price: 825, change: +1,  history: [821, 820, 822, 823, 824, 824, 825] },
    LNG:   { price: null, change: null, history: null },
  },
  PAN: {
    name: 'Panama',
    region: 'Central Americas',
    flag: '🇵🇦',
    lat: 8.99, lng: -79.52,
    VLSFO: { price: 735, change: +5,  history: [724, 723, 725, 728, 730, 730, 735] },
    HSFO:  { price: 462, change: +4,  history: [454, 453, 455, 457, 458, 458, 462] },
    MGO:   { price: 851, change: +7,  history: [839, 838, 840, 843, 846, 844, 851] },
    LNG:   { price: null, change: null, history: null },
  },
  DUR: {
    name: 'Durban',
    region: 'Southern Africa',
    flag: '🇿🇦',
    lat: -29.86, lng: 31.02,
    // Elevated — Cape rerouting demand surge
    VLSFO: { price: 741, change: +6,  history: [728, 729, 731, 734, 737, 735, 741] },
    HSFO:  { price: 465, change: +7,  history: [451, 452, 455, 458, 460, 458, 465] },
    MGO:   { price: 856, change: +8,  history: [841, 840, 843, 847, 851, 848, 856] },
    LNG:   { price: null, change: null, history: null },
  },
  CPT: {
    name: 'Cape Town',
    region: 'Southern Africa',
    flag: '🇿🇦',
    lat: -33.92, lng: 18.42,
    // Elevated — Cape rerouting demand surge
    VLSFO: { price: 738, change: +4,  history: [729, 728, 730, 733, 735, 734, 738] },
    HSFO:  { price: 463, change: +5,  history: [451, 450, 453, 456, 458, 458, 463] },
    MGO:   { price: 853, change: +5,  history: [841, 840, 843, 846, 849, 848, 853] },
    LNG:   { price: null, change: null, history: null },
  },
  CMB: {
    name: 'Colombo',
    region: 'South Asia',
    flag: '🇱🇰',
    lat: 6.93, lng: 79.85,
    VLSFO: { price: 724, change: -3,  history: [733, 734, 732, 730, 729, 727, 724] },
    HSFO:  { price: 455, change: -2,  history: [461, 460, 459, 458, 457, 457, 455] },
    MGO:   { price: 841, change: -4,  history: [850, 849, 848, 847, 846, 845, 841] },
    LNG:   { price: null, change: null, history: null },
  },
  JED: {
    name: 'Jeddah',
    region: 'Red Sea',
    flag: '🇸🇦',
    lat: 21.48, lng: 39.17,
    // Elevated — Red Sea conflict risk premium
    VLSFO: { price: 755, change: +9,  history: [736, 737, 739, 743, 748, 746, 755] },
    HSFO:  { price: 468, change: +10, history: [449, 450, 453, 457, 461, 458, 468] },
    MGO:   { price: 874, change: +11, history: [851, 850, 854, 860, 866, 863, 874] },
    LNG:   { price: null, change: null, history: null },
  },
  MUM: {
    name: 'Mumbai',
    region: 'South Asia',
    flag: '🇮🇳',
    lat: 18.92, lng: 72.84,
    VLSFO: { price: 731, change: +2,  history: [724, 723, 725, 727, 728, 729, 731] },
    HSFO:  { price: 459, change: +3,  history: [452, 451, 453, 455, 456, 456, 459] },
    MGO:   { price: 847, change: +4,  history: [838, 837, 840, 842, 844, 843, 847] },
    LNG:   { price: null, change: null, history: null },
  },
  SHA: {
    name: 'Shanghai',
    region: 'Asia Pacific',
    flag: '🇨🇳',
    lat: 31.23, lng: 121.47,
    VLSFO: { price: 716, change: -5,  history: [727, 728, 726, 724, 722, 721, 716] },
    HSFO:  { price: 449, change: -3,  history: [455, 454, 453, 452, 451, 452, 449] },
    MGO:   { price: 832, change: -6,  history: [842, 843, 841, 839, 838, 838, 832] },
    LNG:   { price: 684, change: -1,  history: [689, 688, 687, 686, 685, 685, 684] },
  },
  BUS: {
    name: 'Busan',
    region: 'Asia Pacific',
    flag: '🇰🇷',
    lat: 35.18, lng: 129.08,
    VLSFO: { price: 719, change: -4,  history: [729, 730, 728, 726, 724, 723, 719] },
    HSFO:  { price: 452, change: -2,  history: [457, 456, 455, 454, 454, 454, 452] },
    MGO:   { price: 835, change: -5,  history: [844, 845, 843, 841, 840, 840, 835] },
    LNG:   { price: 688, change: +2,  history: [683, 682, 684, 685, 686, 686, 688] },
  },
  HKG: {
    name: 'Hong Kong',
    region: 'Asia Pacific',
    flag: '🇭🇰',
    lat: 22.30, lng: 114.17,
    VLSFO: { price: 722, change: -3,  history: [731, 732, 730, 728, 727, 725, 722] },
    HSFO:  { price: 453, change: -1,  history: [457, 456, 455, 454, 454, 454, 453] },
    MGO:   { price: 838, change: -4,  history: [846, 847, 845, 843, 842, 842, 838] },
    LNG:   { price: null, change: null, history: null },
  },
  KHH: {
    name: 'Kaohsiung',
    region: 'Asia Pacific',
    flag: '🇹🇼',
    lat: 22.62, lng: 120.30,
    VLSFO: { price: 721, change: -2,  history: [727, 728, 726, 725, 724, 723, 721] },
    HSFO:  { price: 452, change:  0,  history: [451, 452, 453, 452, 451, 452, 452] },
    MGO:   { price: 836, change: -3,  history: [843, 844, 842, 840, 839, 839, 836] },
    LNG:   { price: null, change: null, history: null },
  },
  BAL: {
    name: 'Balboa',
    region: 'Central Americas',
    flag: '🇵🇦',
    lat: 8.95, lng: -79.57,
    VLSFO: { price: 733, change: +4,  history: [723, 722, 724, 727, 729, 729, 733] },
    HSFO:  { price: 460, change: +3,  history: [452, 451, 453, 455, 457, 457, 460] },
    MGO:   { price: 849, change: +6,  history: [837, 836, 839, 842, 845, 843, 849] },
    LNG:   { price: null, change: null, history: null },
  },
  LPA: {
    name: 'Las Palmas',
    region: 'Atlantic',
    flag: '🇪🇸',
    lat: 28.14, lng: -15.43,
    VLSFO: { price: 705, change: -2,  history: [711, 712, 710, 709, 708, 707, 705] },
    HSFO:  { price: 443, change: +1,  history: [439, 440, 441, 442, 442, 442, 443] },
    MGO:   { price: 822, change: +1,  history: [818, 817, 819, 820, 821, 821, 822] },
    LNG:   { price: null, change: null, history: null },
  },
  TPP: {
    name: 'Tanjung Pelepas',
    region: 'Asia Pacific',
    flag: '🇲🇾',
    lat: 1.36, lng: 103.55,
    VLSFO: { price: 725, change: -6,  history: [738, 740, 738, 735, 732, 731, 725] },
    HSFO:  { price: 456, change: -4,  history: [463, 462, 461, 460, 459, 460, 456] },
    MGO:   { price: 843, change: -7,  history: [854, 856, 854, 851, 850, 850, 843] },
    LNG:   { price: null, change: null, history: null },
  },
};

export const PORT_ORDER = [
  'SGP','FUJ','RTM','HOU','PIR','GIB','ALG','PAN',
  'DUR','CPT','CMB','JED','MUM','SHA','BUS','HKG','KHH','BAL','LPA','TPP',
];

export const FUEL_TYPES = ['VLSFO', 'HSFO', 'MGO', 'LNG', 'B20', 'B30'];

export const FUEL_META = {
  VLSFO: { label: 'VLSFO 0.5%', unit: '$/mt', description: 'Very Low Sulphur Fuel Oil — IMO 2020 compliant' },
  HSFO:  { label: 'HSFO 3.5%',  unit: '$/mt', description: 'High Sulphur Fuel Oil — scrubber-fitted vessels only' },
  MGO:   { label: 'MGO 0.1%',   unit: '$/mt', description: 'Marine Gas Oil — ECA zones required' },
  LNG:   { label: 'LNG',        unit: '$/mt', description: 'Liquefied Natural Gas — select ports only' },
  B20:   { label: 'B20 Biofuel',unit: '$/mt', description: '20% UCOME biofuel blend with VLSFO — FuelEU compliant' },
  B30:   { label: 'B30 Biofuel',unit: '$/mt', description: '30% UCOME biofuel blend with VLSFO — deeper GHG reduction' },
};

// B20/B30 availability: select major ports only (Apr 2026)
// Price = VLSFO + biofuel premium. B20 ~+$95/mt, B30 ~+$155/mt above VLSFO
// Availability limited by UCOME supply chains — Rotterdam and Singapore are main hubs
export const BIOFUEL_PORTS = {
  SGP: {
    B20: { price: 823, change: -6,  history: [838, 842, 840, 837, 834, 829, 823], available: true },
    B30: { price: 883, change: -8,  history: [900, 904, 901, 897, 893, 891, 883], available: true },
  },
  RTM: {
    B20: { price: 789, change: -3,  history: [797, 799, 796, 794, 793, 792, 789], available: true },
    B30: { price: 849, change: -4,  history: [858, 860, 857, 855, 854, 853, 849], available: true },
  },
  HOU: {
    B20: { price: 797, change: +4,  history: [789, 790, 792, 793, 794, 793, 797], available: true },
    B30: { price: 857, change: +5,  history: [848, 849, 851, 852, 853, 852, 857], available: true },
  },
  PIR: {
    B20: { price: 813, change: -1,  history: [817, 818, 817, 816, 815, 814, 813], available: true },
    B30: { price: 873, change: -2,  history: [878, 879, 878, 877, 876, 875, 873], available: true },
  },
  GIB: {
    B20: { price: 806, change: -1,  history: [810, 811, 810, 809, 808, 807, 806], available: true },
    B30: { price: 866, change: -1,  history: [870, 871, 870, 869, 868, 867, 866], available: true },
  },
  SHA: {
    B20: { price: 811, change: -4,  history: [820, 822, 820, 818, 816, 815, 811], available: true },
    B30: { price: 871, change: -5,  history: [881, 883, 880, 877, 875, 874, 871], available: true },
  },
  BUS: {
    B20: { price: 814, change: -3,  history: [822, 824, 822, 820, 818, 817, 814], available: true },
    B30: { price: 874, change: -4,  history: [883, 885, 883, 881, 879, 878, 874], available: true },
  },
};

// Merge biofuel data into ports
Object.keys(BIOFUEL_PORTS).forEach(portId => {
  if (PORTS_DATA[portId]) {
    PORTS_DATA[portId].B20 = BIOFUEL_PORTS[portId].B20;
    PORTS_DATA[portId].B30 = BIOFUEL_PORTS[portId].B30;
  }
});
// Ports without biofuel availability get null entries
Object.keys(PORTS_DATA).forEach(portId => {
  if (!PORTS_DATA[portId].B20) {
    PORTS_DATA[portId].B20 = { price: null, change: null, history: null, available: false };
    PORTS_DATA[portId].B30 = { price: null, change: null, history: null, available: false };
  }
});
export const ROUTES = {
  ag_fareast: {
    label: 'AG → Far East',
    ports: ['FUJ', 'CMB', 'SGP', 'TPP'],
    description: 'Arabian Gulf to East Asia · Suez or Cape option',
  },
  europe_asia_suez: {
    label: 'Europe → Asia (Suez)',
    ports: ['RTM', 'GIB', 'PIR', 'JED', 'CMB', 'SGP'],
    description: 'North Europe to Asia via Suez Canal',
  },
  europe_asia_cape: {
    label: 'Europe → Asia (Cape)',
    ports: ['RTM', 'LPA', 'CPT', 'DUR', 'SGP', 'TPP'],
    description: 'North Europe to Asia via Cape of Good Hope',
  },
  usgulf_europe: {
    label: 'US Gulf → Europe',
    ports: ['HOU', 'LPA', 'GIB', 'ALG', 'RTM'],
    description: 'Trans-Atlantic — crude / clean products',
  },
  transpac_panama: {
    label: 'Trans-Pacific (Panama)',
    ports: ['HOU', 'BAL', 'SGP', 'SHA', 'BUS'],
    description: 'US East / Gulf to Asia via Panama Canal',
  },
  transpac_direct: {
    label: 'Trans-Pacific (Direct)',
    ports: ['BAL', 'HKG', 'SHA', 'BUS'],
    description: 'US West Coast to North Asia — containers / autos',
  },
  westafr_europe: {
    label: 'West Africa → Europe',
    ports: ['LPA', 'GIB', 'RTM'],
    description: 'West African crude exports to ARA',
  },
  westafr_asia: {
    label: 'West Africa → Asia',
    ports: ['LPA', 'CPT', 'DUR', 'SGP', 'TPP'],
    description: 'West African crude to Asian refineries via Cape',
  },
  med_ag: {
    label: 'Med → Arabian Gulf',
    ports: ['PIR', 'ALG', 'GIB', 'JED', 'FUJ'],
    description: 'Clean products / chemicals Med to AG',
  },
  baltic_fareast: {
    label: 'Baltic → Far East',
    ports: ['RTM', 'GIB', 'CPT', 'DUR', 'SGP', 'SHA'],
    description: 'Dry bulk and products via Cape of Good Hope',
  },
  australia_china: {
    label: 'Australia → China',
    ports: ['SGP', 'HKG', 'SHA'],
    description: 'Capesize iron ore and coal trade',
  },
  usgulf_fareast: {
    label: 'US Gulf → Far East',
    ports: ['HOU', 'BAL', 'SGP', 'SHA', 'BUS'],
    description: 'Grain / clean products via Panama to Asia',
  },
  india_europe: {
    label: 'India → Europe',
    ports: ['MUM', 'CMB', 'GIB', 'ALG', 'RTM'],
    description: 'Indian subcontinent to NW Europe',
  },
  ag_eastafr: {
    label: 'AG → East Africa',
    ports: ['FUJ', 'MUM', 'CMB', 'DUR'],
    description: 'Arabian Gulf to East Africa — regional tankers',
  },
};

// ─── Forward Curves (mock — April 2026) ──────────────────────────────────────
// VLSFO 3-month forward prices for key ports
// Showing slight contango (market pricing in Hormuz risk premium)
// Source: Would come from ZeroNorth Bunker Pricer or ENGINE API in production
export const FORWARD_CURVES = {
  SGP: {
    name: 'Singapore', flag: '🇸🇬',
    spot: 728,
    curve: [
      { month: 'May 2026',  price: 734, change: +6  },
      { month: 'Jun 2026',  price: 741, change: +7  },
      { month: 'Jul 2026',  price: 748, change: +7  },
      { month: 'Q3 2026',   price: 752, change: +4  },
      { month: 'Q4 2026',   price: 745, change: -7  },
    ],
    structure: 'contango',  // market expects prices to rise
    note: 'Iran-US tension risk premium baked into forward curve',
  },
  RTM: {
    name: 'Rotterdam', flag: '🇳🇱',
    spot: 694,
    curve: [
      { month: 'May 2026',  price: 698, change: +4  },
      { month: 'Jun 2026',  price: 703, change: +5  },
      { month: 'Jul 2026',  price: 706, change: +3  },
      { month: 'Q3 2026',   price: 710, change: +4  },
      { month: 'Q4 2026',   price: 702, change: -8  },
    ],
    structure: 'contango',
    note: 'Seasonal demand increase expected Q3 · Cape rerouting pressure',
  },
  FUJ: {
    name: 'Fujairah', flag: '🇦🇪',
    spot: 762,
    curve: [
      { month: 'May 2026',  price: 771, change: +9  },
      { month: 'Jun 2026',  price: 778, change: +7  },
      { month: 'Jul 2026',  price: 774, change: -4  },
      { month: 'Q3 2026',   price: 768, change: -6  },
      { month: 'Q4 2026',   price: 751, change: -17 },
    ],
    structure: 'hump',  // near-term premium then rolloff
    note: 'Near-term spike on Iran risk · Market pricing de-escalation by Q4',
  },
  HOU: {
    name: 'Houston', flag: '🇺🇸',
    spot: 702,
    curve: [
      { month: 'May 2026',  price: 706, change: +4  },
      { month: 'Jun 2026',  price: 711, change: +5  },
      { month: 'Jul 2026',  price: 714, change: +3  },
      { month: 'Q3 2026',   price: 712, change: -2  },
      { month: 'Q4 2026',   price: 705, change: -7  },
    ],
    structure: 'contango',
    note: 'US Gulf refinery run rates stabilising · Brent forward curve support',
  },
  SHA: {
    name: 'Shanghai', flag: '🇨🇳',
    spot: 716,
    curve: [
      { month: 'May 2026',  price: 719, change: +3  },
      { month: 'Jun 2026',  price: 724, change: +5  },
      { month: 'Jul 2026',  price: 728, change: +4  },
      { month: 'Q3 2026',   price: 731, change: +3  },
      { month: 'Q4 2026',   price: 722, change: -9  },
    ],
    structure: 'contango',
    note: 'China demand recovery · VLSFO supply tightening post-AG disruption',
  },
};


/** Computed metrics from raw data */
export const computeMetrics = (portsData) => {
  const vlsfoPrices = Object.entries(portsData)
    .map(([id, p]) => ({ id, name: p.name, price: p.VLSFO.price, change: p.VLSFO.change }))
    .filter(p => p.price !== null);

  const sorted = [...vlsfoPrices].sort((a, b) => a.price - b.price);
  const globalAvg = Math.round(vlsfoPrices.reduce((s, p) => s + p.price, 0) / vlsfoPrices.length);
  const cheapest  = sorted[0];
  const mostExp   = sorted[sorted.length - 1];
  const biggestMove = [...vlsfoPrices].sort((a, b) => Math.abs(b.change) - Math.abs(a.change))[0];

  return { globalAvg, cheapest, mostExp, biggestMove };
};

// Key ports for the spread heatmap (10 global hubs)
export const HEATMAP_PORTS = ['RTM','LPA','GIB','SGP','TPP','SHA','DUR','FUJ','JED','HOU'];

/** Get mock data with metadata */
export const getMockData = () => ({
  source: 'mock',
  lastUpdated: new Date().toISOString(),
  dataDate: '2026-04-04',
  ports: PORTS_DATA,
  metrics: computeMetrics(PORTS_DATA),
  disclaimer: 'Demo data — representative April 2026 pricing. Connect live feed in Settings.',
});
